export {
  STATE_LABEL,
  FAMILY_OPTIONS,
  FAMILY_OPTIONS_COLLECTION,
} from "../constants/domain";

import type { AgeCategory, StCategory } from "@/@types/price-markdown-selection";
import {
  CYCLE_MODE,
  STATE_LABEL,
  MARKDOWN_STATE,
  ITEM_STATUS_AWAITING_LABEL,
  ITEM_STATUS_COLUMN_LABEL,
  ITEM_STATUS_IN_MARKDOWN_LABEL,
  ITEM_STATUS_NONE_LABEL,
  ITEM_STATUS_ALL_LABEL,
  type CycleMode,
} from "../constants/domain";
import { SELECTION_MESSAGES } from "../constants/messages";
import { PM_TABLE_HEAD_PILL } from "../constants/theme";
import {
  ITEM_STATUS_FILTER,
  TRI_STATE_FILTER,
  type ItemStatusFilter,
} from "@/services/price-markdown/selection";
import type { MarkdownState } from "@/@types/price-markdown";
import type { TriStateFilter } from "@/services/price-markdown/selection";

import type { DrillColumn, RowState } from "./types";

export const DRILL_AGE_LABEL: Record<AgeCategory, string> = {
  UP_TO_30: "Até 30 dias",
  BETWEEN_31_AND_90: "31 a 90 dias",
  BETWEEN_91_AND_120: "91 a 120 dias",
  OVER_120: "Acima de 120 dias",
};

export const DEFAULT_DRILL_CHECKS = {
  seasonalFilter: TRI_STATE_FILTER.OCULTAR,
  kviFilter: TRI_STATE_FILTER.OCULTAR,
  backlogFilter: TRI_STATE_FILTER.OCULTAR,
  repricedFilter: TRI_STATE_FILTER.OCULTAR,
  itemStatusFilter: ITEM_STATUS_FILTER.TODAS,
  negativeMarginOnly: false,
  markedOnly: false,
  suggestedOnly: false,
} as const;

export const TRI_STATE_LABEL: Record<TriStateFilter, string> = {
  OCULTAR: "Ocultar",
  TODOS: "Todos",
  SOMENTE: "Somente",
};

export const TRI_STATE_ORDER: TriStateFilter[] = [
  TRI_STATE_FILTER.OCULTAR,
  TRI_STATE_FILTER.TODOS,
  TRI_STATE_FILTER.SOMENTE,
];

export const ITEM_STATUS_FILTER_LABEL: Record<ItemStatusFilter, string> = {
  TODAS: ITEM_STATUS_ALL_LABEL,
  AGUARDANDO_APLICACAO: ITEM_STATUS_AWAITING_LABEL,
  EM_REMARCACAO: ITEM_STATUS_IN_MARKDOWN_LABEL,
  SEM_SITUACAO: ITEM_STATUS_NONE_LABEL,
};

export const ITEM_STATUS_FILTER_ORDER: ItemStatusFilter[] = [
  ITEM_STATUS_FILTER.TODAS,
  ITEM_STATUS_FILTER.AGUARDANDO_APLICACAO,
  ITEM_STATUS_FILTER.EM_REMARCACAO,
  ITEM_STATUS_FILTER.SEM_SITUACAO,
];

export interface TriStateDimension {
  key: "seasonalFilter" | "kviFilter" | "backlogFilter" | "repricedFilter";
  label: string;
}

export const TRI_STATE_DIMENSIONS: TriStateDimension[] = [
  { key: "seasonalFilter", label: "Sazonal" },
  { key: "kviFilter", label: "KVI" },
  { key: "backlogFilter", label: "Com carteira" },
  { key: "repricedFilter", label: "Remarcados anteriormente" },
];

export interface CollectionStatusOption {
  value: string;
  label: string;
  states: MarkdownState[];
  altered?: boolean;
}

export const COLLECTION_STATUS_OPTIONS: ReadonlyArray<CollectionStatusOption> = [
  ...(
    [
      MARKDOWN_STATE.RASCUNHO,
      MARKDOWN_STATE.SUBMETIDA,
      MARKDOWN_STATE.RECUSADA,
      MARKDOWN_STATE.APROVADA,
      MARKDOWN_STATE.EFETIVADA,
      MARKDOWN_STATE.DESCARTADA,
    ] as MarkdownState[]
  ).map((state) => ({
    value: state,
    label: STATE_LABEL[state],
    states: [state],
  })),
  { value: "ALTERADAS", label: "Alteradas", states: [], altered: true },
];

export const nameWithoutCode = (label: string): string => {
  const separator = label.indexOf("-");
  if (separator < 0) return label;
  const name = label.slice(separator + 1).trim();
  if (name === "") {
    return label;
  }
  return name;
};

export const TAXONOMY_SECTION_SEPARATOR = " › ";

export const SUMMARY_SECTION_SEPARATOR = " · ";

export const labelAmongSiblings = (
  label: string,
  siblings: ReadonlyArray<string>,
): string => {
  const name = nameWithoutCode(label);
  const hasNamesake = siblings.some(
    (sibling) => sibling !== label && nameWithoutCode(sibling) === name,
  );
  if (hasNamesake) {
    return label;
  }
  return name;
};

export const MATRIX_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "New", label: "New" },
  { value: "Best Seller 1", label: "Best Seller 1" },
  { value: "Best Seller 2", label: "Best Seller 2" },
  { value: "Average 1", label: "Average 1" },
  { value: "Average 2", label: "Average 2" },
  { value: "End-of-Life 1", label: "End-of-Life 1" },
  { value: "End-of-Life 2", label: "End-of-Life 2" },
  { value: "Overestimated", label: "Overestimated" },
  { value: "Slow Mover", label: "Slow Mover" },
  { value: "No Mover", label: "No Mover" },
  { value: "Sem entrada CD", label: "Sem entrada CD" },
];

export const ST_CATEGORY_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "AVERAGE", label: "Average" },
  { value: "BELOW_AVERAGE", label: "Below-Average" },
  { value: "ABOVE_AVERAGE", label: "Above-Average" },
];

export const ST_LABEL_DRILL = Object.fromEntries(
  ST_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<StCategory, string>;

export const ST_STATUS_PILL_VARIANT: Record<
  StCategory,
  "up" | "mid" | "down"
> = {
  ABOVE_AVERAGE: "up",
  AVERAGE: "mid",
  BELOW_AVERAGE: "down",
};

export const AGE_BUCKET_BY_AGE_CATEGORY: Record<AgeCategory, string> = {
  UP_TO_30: "Up to 30 Days",
  BETWEEN_31_AND_90: "Between 31 and 90 Days",
  BETWEEN_91_AND_120: "Between 91 and 120 Days",
  OVER_120: "Over 120 Days",
};

export const AGE_STATUS_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  {
    value: AGE_BUCKET_BY_AGE_CATEGORY.UP_TO_30,
    label: SELECTION_MESSAGES.DRILL_STATUS_AGE_UP_TO_30,
  },
  {
    value: AGE_BUCKET_BY_AGE_CATEGORY.BETWEEN_31_AND_90,
    label: SELECTION_MESSAGES.DRILL_STATUS_AGE_BETWEEN_31_AND_90,
  },
  {
    value: AGE_BUCKET_BY_AGE_CATEGORY.BETWEEN_91_AND_120,
    label: SELECTION_MESSAGES.DRILL_STATUS_AGE_BETWEEN_91_AND_120,
  },
  {
    value: AGE_BUCKET_BY_AGE_CATEGORY.OVER_120,
    label: SELECTION_MESSAGES.DRILL_STATUS_AGE_OVER_120,
  },
];

export const CELL_FILTER_SEPARATOR = "-";

export const DRILL_PAGE_SIZE = 50;
export const COLLECTION_PAGE_SIZE = 20;

export interface CollectionCycleFilter {
  cycleMode?: CycleMode;
  cycleMonth?: string;
  cycleFrom?: string;
  cycleTo?: string;
}

export const DEFAULT_COLLECTION_CYCLE: CollectionCycleFilter = {
  cycleMode: CYCLE_MODE.ATUAL,
};

export const NEW_MARKDOWN_ID = "novo";

export const DRILL_URL_PARAM = {
  FAMILY: "familia",
  CYCLE: "ciclo",
  SOURCE_FAMILY: "familiaOrigem",
  CELL_FILTER: "filtroCelula",
} as const;

export const DRILL_COLUMNS: ReadonlyArray<DrillColumn> = [
  { key: "code", label: "Código", width: "w-[120px]", sortKey: "sku" },
  { key: "description", label: "Descrição", width: "w-[220px]", sortKey: "description" },
  { key: "itemStatus", label: ITEM_STATUS_COLUMN_LABEL, width: "w-[150px]" },
  { key: "family", label: "Família", width: "w-[110px]", sortKey: "family" },
  { key: "department", label: "Departamento", width: "w-[170px]", sortKey: "department" },
  { key: "group", label: "Grupo", width: "w-[150px]", sortKey: "group" },
  { key: "subgroup", label: "Subgrupo", width: "w-[160px]", sortKey: "subgroup" },
  { key: "seasonal", label: "Sazonal", width: "w-[90px]" },
  { key: "kvi", label: "KVI", width: "w-[70px]" },
  { key: "onPromotion", label: "Item Sob Promoção", width: "w-[140px]" },
  { key: "lastRepricing", label: "Últ. Remarcação", width: "w-[130px]", sortKey: "lastRepricing" },
  { key: "totalBacklog", label: "Carteira", width: "w-[100px]", align: "right", sortKey: "totalBacklog" },
  { key: "stockDays", label: "Idade", width: "w-[90px]", align: "right", sortKey: "stockDays" },
  { key: "stockQty", label: "Estoque", width: "w-[100px]", align: "right", sortKey: "stockQty" },
  { key: "stockBrl", label: "R$ Estoque", width: "w-[130px]", align: "right", sortKey: "stockBrl" },
  { key: "totalSales", label: "Vendas Totais", width: "w-[120px]", align: "right", sortKey: "totalSales" },
  { key: "me", label: "ME", width: "w-[100px]", align: "right" },
  { key: "mv", label: "MV", width: "w-[100px]", align: "right" },
  { key: "lowestHistorical", label: "Menor Preço em Loja", width: "w-[160px]", align: "right", sortKey: "lowestHistorical" },
  { key: "currentEffectiveAvgPrice", label: "PME Atual", width: "w-[120px]", align: "right", sortKey: "currentEffectiveAvgPrice" },
  { key: "discount", label: "Desconto", width: "w-[120px]" },
  { key: "finalPrice", label: "R$ Preço Final", width: "w-[130px]", align: "right" },
  { key: "markdownValue", label: "R$ Remarcação", width: "w-[130px]", align: "right" },
  { key: "nme", label: "NME", width: "w-[100px]", align: "right" },
  { key: "st", label: "ST", width: "w-[110px]", sortKey: "stPercent", labelClass: PM_TABLE_HEAD_PILL },
  { key: "stStatus", label: "Status ST", width: "w-[150px]", labelClass: PM_TABLE_HEAD_PILL },
  { key: "ageStatus", label: "Status Idade", width: "w-[150px]", sortKey: "ageCategory", labelClass: PM_TABLE_HEAD_PILL },
  { key: "matrix", label: "Matriz", width: "w-[130px]", sortKey: "stCategory", labelClass: PM_TABLE_HEAD_PILL },
];

export const DRILL_SELECTION_COL_WIDTH = 44;
export const DRILL_SUGGESTION_COL_WIDTH = 130;
export const DRILL_MARKDOWN_COL_WIDTH = 140;

export const DRILL_TAB = {
  DETAIL: "detail",
  INDICATORS: "indicators",
} as const;

export const ROW_STATE = {
  NME_NEGATIVA: "NME_NEGATIVA",
  REMARCAR: "REMARCAR",
  SUGESTAO: "SUGESTAO",
  NEUTRO: "NEUTRO",
} as const;

export const ROW_STATE_CLASS: Record<RowState, string> = {
  NME_NEGATIVA: "bg-red-50 text-red-900",
  REMARCAR: "bg-yellow-50",
  SUGESTAO: "bg-violet-50",
  NEUTRO: "",
};

export const drillTableMinWidth = (withSelection: boolean): number => {
  let selectionWidth = 0;
  if (withSelection) {
    selectionWidth = DRILL_SELECTION_COL_WIDTH;
  }
  return (
    DRILL_COLUMNS.reduce(
      (total, col) => total + Number(col.width.replace(/\D/g, "")),
      0,
    ) +
    selectionWidth +
    DRILL_SUGGESTION_COL_WIDTH +
    DRILL_MARKDOWN_COL_WIDTH
  );
};
